import React from 'react';
import { motion } from 'framer-motion';

const AmbientBackground = ({ currentScene, totalScenes = 11 }) => {
  // Subtle color shifts based on scene
  // Phase 0 (1-5): Deep void with subtle warmth
  // Phase I (6-11): Slightly deeper, more contained feeling
  const getGradient = () => {
    const gradients = [
      // Scene 1 - Arrival: Deep void with subtle warmth
      'radial-gradient(ellipse at 50% 50%, hsl(240, 6%, 6%) 0%, hsl(240, 8%, 3%) 100%)',
      // Scene 2 - Orientation: Slight shift toward focus
      'radial-gradient(ellipse at 30% 70%, hsl(240, 6%, 7%) 0%, hsl(240, 8%, 3%) 100%)',
      // Scene 3 - Centering: Centered, contained
      'radial-gradient(ellipse at 50% 50%, hsl(38, 10%, 6%) 0%, hsl(240, 8%, 3%) 100%)',
      // Scene 4 - Declaration: Authority, slight glow
      'radial-gradient(ellipse at 50% 40%, hsl(38, 12%, 7%) 0%, hsl(240, 8%, 3%) 100%)',
      // Scene 5 - Transition: Threshold, opening
      'radial-gradient(ellipse at 50% 30%, hsl(38, 8%, 5%) 0%, hsl(240, 8%, 3%) 100%)',
      // Phase I begins - Scene 6 - Stillness Holds: Settling deeper
      'radial-gradient(ellipse at 50% 50%, hsl(240, 8%, 5%) 0%, hsl(240, 10%, 2%) 100%)',
      // Scene 7 - Recognition: Quiet awareness
      'radial-gradient(ellipse at 40% 50%, hsl(240, 7%, 5%) 0%, hsl(240, 10%, 2%) 100%)',
      // Scene 8 - Internal Boundary: Containment tightens
      'radial-gradient(ellipse at 50% 50%, hsl(240, 8%, 4%) 0%, hsl(240, 10%, 2%) 100%)',
      // Scene 9 - Severance: Authority settles
      'radial-gradient(ellipse at 50% 45%, hsl(35, 8%, 5%) 0%, hsl(240, 10%, 2%) 100%)',
      // Scene 10 - Holding State: Vault active - subtle warmth
      'radial-gradient(ellipse at 50% 50%, hsl(35, 10%, 6%) 0%, hsl(240, 10%, 2%) 100%)',
      // Scene 11 - Transition Prompt: Opening to choice
      'radial-gradient(ellipse at 50% 40%, hsl(38, 8%, 5%) 0%, hsl(240, 10%, 2%) 100%)'
    ];
    return gradients[currentScene] || gradients[0];
  };

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Base gradient layer */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: 1,
          background: getGradient()
        }}
        transition={{ 
          duration: 4, 
          ease: [0.25, 0.1, 0.25, 1] 
        }}
      />

      {/* Subtle vignette */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 0%, hsl(240, 8%, 2%) 90%)'
        }}
      />

      {/* Noise texture overlay - very subtle */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      {/* Breathing glow - very subtle, almost imperceptible */}
      {/* Slightly more prominent during holding state (scene 10) */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
        style={{
          background: currentScene >= 9 
            ? 'radial-gradient(circle, hsl(38, 25%, 50%, 0.04) 0%, transparent 70%)'
            : 'radial-gradient(circle, hsl(38, 30%, 50%, 0.03) 0%, transparent 70%)'
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: currentScene >= 9 ? [0.4, 0.6, 0.4] : [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: currentScene >= 9 ? 10 : 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
    </div>
  );
};

export default AmbientBackground;
